import React, { useEffect, useState, useRef } from 'react';
import { connect } from 'react-redux';
import { FormattedMessage, FormattedDate, FormattedTime } from 'react-intl';
import SvgExclamationPointIcon from '../svg/ExclamationPointIcon';
import {
  loadMessageDetails,
  cancelMessage,
} from '../../redux/actions/messagesActions';
import MessagesMessageViewTable from './MessagesMessageViewTable';
import {
  Button,
  buttonSizes,
  buttonTypes,
  buttonTextSizes,
} from '../shared/Button';
import { VictoryPie, VictoryLegend, VictoryTooltip, VictoryChart } from 'victory';
import { resetText } from '../shared/SharedInternationalizations';

export function UnconnectedMessagesMessageView(props) {
  const [messageFilters, setMessageFilters] = useState({
    currentMessage: null,
    deviceFilters: {
      delivered: true,
      undelivered: true,
      acknowledged: true,
      detailsSearch: '',
    },
  });

  const { message, msgDetails } = props;

  let messageTitle = 'No Title';
  if (message.title) {
    messageTitle = message.title;
  }

  let cancelButton = null;
  if (message.messageStatus !== 'Cancelled') {
    cancelButton = (
      <Button
        data-test="cancel-button"
        onClick={cancelCurrentMessage}
        text="Cancel Message"
      />
    );
  }

  /**
   * Function to set which filters are in use for the displayed table of responses
   * @param {object} event - The click event for selecting the filter options
   * @fires setState - Updates local state with the filter information
   */
  function setFilterType(event) {
    const value = event.target.name;
    let filters = messageFilters.deviceFilters;
    // Check to see if the filter value is set to true, if so it is set to false. If it's not true, set it to true
    if (filters[value]) {
      setMessageFilters({
        ...messageFilters,
        deviceFilters: {
          ...messageFilters.deviceFilters,
          [value]: false,
        },
      });
    } else {
      setMessageFilters({
        ...messageFilters,
        deviceFilters: {
          ...messageFilters.deviceFilters,
          [value]: true,
        },
      });
    }
  }

  /**
   * Function to set the search value for active filtering
   * @param {object} event - The event from typing in the value to the search bar
   * @fires setState - Updates local state with the value typed into the search bar
   */
  function searchFilter(event) {
    const value = event.target.value;
    setMessageFilters({
      ...messageFilters,
      deviceFilters: {
        ...messageFilters.deviceFilters,
        detailsSearch: value,
      },
    });
  }

  /**
   * Function to reset the values of the search/filter bar to display everything
   * @fires setState - Returns state to its default settings
   */
  function resetFilters() {
    setMessageFilters({
      ...messageFilters,
      deviceFilters: {
        delivered: true,
        undelivered: true,
        acknowledged: true,
        detailsSearch: '',
      },
    });
  }

  /**
   * Function to create the pie chart display
   * @returns - SVG pie chart
   */
function chartView() {
    // Early return with null if no data - fixes React 17 VictoryPortal error
    if (!msgDetails || msgDetails.length === 0) {
      return null;
    }

    let delivered = [];
    // Filter down to just delivered items
    delivered = msgDetails.filter((item) => item.msg_status === 'Delivered');
    // filter down to just undelivered items
    let undelivered = [];
    undelivered = msgDetails.filter(
      (item) => item.msg_status === 'Undelivered'
    );

    let acknowledged = [];
    // filter down to just acknowledged items
    acknowledged = msgDetails.filter(
      (item) => item.msg_status === 'Acknowledged'
    );
    // Count the total items in the response array
    const total = msgDetails.length;
    // count the delivered items
    const deliveredCount = delivered.length;
    // calculate the persentage of the total that are delivered
    const deliveredPercent = (deliveredCount / total) * 100;
    const undeliveredCount = undelivered.length;
    const undeliveredPercent = (undeliveredCount / total) * 100;
    const acknowledgedCount = acknowledged.length;
    const acknowledgedPercent = (acknowledgedCount / total) * 100;

    // Ensure we have valid data for the chart
    const chartData = [
    { x: undeliveredPercent.toFixed(1) + '%', y: undeliveredCount || 0 },
    { x: deliveredPercent.toFixed(1) + '%', y: deliveredCount || 0 },
    { x: acknowledgedPercent.toFixed(1) + '%', y: acknowledgedCount || 0 },
  ].filter(item => item.y > 0);

  const legendData = [
    { name: 'Undelivered: ' + undeliveredCount },
    { name: 'Delivered: ' + deliveredCount },
    { name: 'Acknowledged: ' + acknowledgedCount },
  ];

  if (chartData.length === 0) {
    return <div>No message data available</div>;
  }

   try {
    return (
    <div style={{ width: 400, height: 300 }}>
      <div data-test="messages-pie-chart">
        <VictoryChart
          width={400}
          height={300}
          padding={{ left: 25, right: 190, top: 50, bottom: 50 }}
        >
        
        <VictoryLegend
          x={235}
          y={50}
          gutter={20}
          title={'Total: ' + total}
          centerTitle
          style={{ border: { stroke: 'black' } }}
          data={legendData}
          colorScale={['#FF9900', '#DC3912', '#3366CC']}
        />
        <VictoryPie
          radius={90}
          labelRadius={75}
          origin={{ y: 150 }}
          data={chartData}
          colorScale={['#FF9900', '#DC3912', '#3366CC'].slice(0, chartData.length)}
          innerRadius={60}
          labelComponent={<VictoryTooltip />}
        />
       </VictoryChart>
     </div>
    </div>
    );
  } catch (error) {
    console.error('Victory chart error:', error);
    return <div>Chart unavailable</div>;
  }
}

  /**
   * Function to return the response table with the filter/search bar
   * @returns {object} - The table of responses with a filter/search bar on top
   */
  function messageTable() {
  // Early return with null if no data - fixes React 17 error
  if (!msgDetails) {
    return null;
  }

  
  return (
    <React.Fragment>
      <div className="message-table__filter-search-bar table__header padding-10">
        <div className="message-table__filter-options">
          <FormattedMessage
            id="messagesMessageView.detailsFilterTitle"
            defaultMessage="Filter By Status: "
            description="Filter by messages that are undelivered">
            {(txt) => <span className="text-bold">{txt}</span>}
          </FormattedMessage>
          <div className="message-table__filter-option">
            <input
              type="checkbox"
              id="undelivered"
              name="undelivered"
              onChange={setFilterType}
              checked={messageFilters.deviceFilters.undelivered}
              className="margin-left-5"
            />
            <FormattedMessage
              id="messagesMessageView.detailsFilterUndelivered"
              defaultMessage="Undelivered"
              description="Filter by messages that are undelivered">
              {(txt) => (
                <label htmlFor="undelivered" className="margin-left-5">
                  {txt}
                </label>
              )}
            </FormattedMessage>
          </div>
          <div className="message-table__filter-option">
            <input
              type="checkbox"
              id="delivered"
              name="delivered"
              onChange={setFilterType}
              checked={messageFilters.deviceFilters.delivered}
              className="margin-left-5"
            />
            <FormattedMessage
              id="messagesMessageView.detailsFilterDelivered"
              defaultMessage="Delivered"
              description="Filter by messages that are delivered">
              {(txt) => (
                <label htmlFor="delivered" className="margin-left-5">
                  {txt}
                </label>
              )}
            </FormattedMessage>
          </div>
          <div className="message-table__filter-option">
            <input
              type="checkbox"
              id="acknowledged"
              name="acknowledged"
              onChange={setFilterType}
              checked={messageFilters.deviceFilters.acknowledged}
              className="margin-left-5"
            />
            <FormattedMessage
              id="messagesMessageView.detailsFilterAcknowledged"
              defaultMessage="Acknowledged"
              description="Filter by messages that are acknowledged">
              {(txt) => (
                <label htmlFor="acknowledged" className="margin-left-5">
                  {txt}
                </label>
              )}
            </FormattedMessage>
          </div>
        </div>
        <div className="message-table__filter-search">
          <FormattedMessage
            id="messagesMessageView.detailsFilterSearchDevices"
            defaultMessage="Search Devices:"
            description="Filter by devices">
            {(txt) => (
              <span className="margin-right-5 text-bold text-medium">
                {txt}
              </span>
            )}
          </FormattedMessage>
          <input
            type="text"
            id="detailsSearch"
            name="detailsSearch"
            onChange={searchFilter}
            className="message-table__filter-search-box"
            value={messageFilters.deviceFilters.detailsSearch}
          />
          <Button
            onClick={resetFilters}
            buttonType={buttonTypes.secondary}
            buttonSize={buttonSizes.extraSmall}
            text={resetText}
            buttonTextSize={buttonTextSizes.small}
            additionalStyles="no-margin"
          />
        </div>
      </div>
      <MessagesMessageViewTable
        details={msgDetails}
        filters={messageFilters.deviceFilters}
      />
    </React.Fragment>
  );
}

  /**
   * Function to trigger refreshing the details results from the api
   * @fires dispatch#loadMessageDetails - Triggers the api call
   */
  // function refreshResults() {
  //   props.dispatch(loadMessageDetails(props.message.id));
  // }

  function cancelCurrentMessage() {
    props.dispatch(cancelMessage(message.id));
  }

  let interval = useRef();
  function refreshResults() {
    props.dispatch(loadMessageDetails(message.id));
  }
  useEffect(() => {
    interval.current = setInterval(refreshResults, 3000);
  }, []); // eslint-disable-line

  useEffect(() => {
    if (message.id !== messageFilters.currentMessage) {
      setMessageFilters({
        ...messageFilters,
        currentMessage: message.id,
        deviceFilters: {
          delivered: true,
          undelivered: true,
          acknowledged: true,
          detailsSearch: '',
        },
      });
    }

    return () => {
      clearTimeout(interval.current);
    };
  }, [messageFilters, message.id]);

  return (
    <div className="message-view__message-container">
      <div className="message-view__message-header">
        <div className="message-header__message-title message-header__item align-center width-100">
          <h1 data-test="message-title" className="text-center no-margin">
            {messageTitle}
          </h1>
          {cancelButton}
        </div>
        {message.urgent ? (
          <span
            className="message-header__item message-header__message-title message-header__urgent text-bold "
            data-test="message-header-urgent">
            <SvgExclamationPointIcon
              className="margin-right-5"
              data-test="component-svg-exclamation-point-icon"
            />
            <FormattedMessage
              id="messagesMessageView.messageUrgent"
              defaultMessage="Urgent"
              description="The message is urgent"
            />
          </span>
        ) : null}
        <p className="message-header__item no-margin">
          <FormattedMessage
            id="messagesMessageView.messageStart"
            defaultMessage="Start:"
            description="The start time for the message">
            {(txt) => <span className="text-bold margin-right-5">{txt} </span>}
          </FormattedMessage>
          <FormattedDate
            value={new Date(message.startTime)}
            year="numeric"
            month="2-digit"
            day="2-digit"
          />
          <span className="margin-left-10">
            <FormattedTime value={new Date(message.startTime)} timeZone="UTC" />
          </span>
        </p>
        <p className="message-header__item no-margin">
          <FormattedMessage
            id="messagesMessageView.messageEnd"
            defaultMessage="End:"
            description="The end time for the message">
            {(txt) => <span className="text-bold margin-right-5">{txt} </span>}
          </FormattedMessage>
          {message.completeTime ? (
            <React.Fragment>
              <FormattedDate
                value={new Date(message.completeTime)}
                year="numeric"
                month="2-digit"
                day="2-digit"
              />
              <span className="margin-left-10">
                <FormattedTime
                  value={new Date(message.completeTime)}
                  timeZone="UTC"
                />
              </span>
            </React.Fragment>
          ) : null}
        </p>
        <p className="message-header__item no-margin">
          <FormattedMessage
            id="messagesMessageView.messageOriginator"
            defaultMessage="Originator: "
            description="The entity that sent the message">
            {(txt) => <span className="text-bold margin-right-5">{txt} </span>}
          </FormattedMessage>

          {message.msgSender}
        </p>
        <p className="message-header__item no-margin">
          <FormattedMessage
            id="messagesMessageView.messageStatus"
            defaultMessage="Status: "
            description="The status of the message">
            {(txt) => <span className="text-bold margin-right-5">{txt}</span>}
          </FormattedMessage>
          <span>{message.messageStatus}</span>
        </p>
      </div>
      <div className="message-view__message-info">
        <div className="message-view__message-content">
          <FormattedMessage
            id="messagesMessageView.messageBodyHeader"
            defaultMessage="Message Body: "
            description="The header indicating this is the body of the message">
            {(txt) => <span className="text-bold">{txt}</span>}
          </FormattedMessage>
          {message.message}
        </div>
        <div className="message-view__message-chart">{chartView()}</div>
      </div>
      <div className="message-view__message-table">{messageTable()}</div>
    </div>
  );
}

const mapStateToProps = (state, ownProps) => {
  const type = ownProps.openMessage.msgType;
  const msgId = ownProps.openMessage.msgId;
  const detailsId = ownProps.openMessage.detailsId;
  const messages = state.messages[type];

  const message = messages[msgId];
  const msgDetails = state.messages.messageDetails[detailsId];

  return {
    message,
    msgDetails,
  };
};

export default connect(mapStateToProps)(UnconnectedMessagesMessageView);
